import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getStepLabel } from "../utils/follow-up-steps";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function DashboardAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [resolvedCases, setResolvedCases] = useState([]);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [resolvedTotalPages, setResolvedTotalPages] = useState(1);
  const [resolvedByFilter, setResolvedByFilter] = useState("");
  const [resolvedSearch, setResolvedSearch] = useState("");
  const [resolvedQuery, setResolvedQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    const response = await api.get("/dashboard/analytics");
    setData(response.data);
  }

  const fetchResolved = useCallback(async (pageNumber = 1) => {
    const params = new URLSearchParams();
    params.set("page", String(pageNumber));
    params.set("limit", "10");

    if (resolvedByFilter) {
      params.set("resolvedBy", resolvedByFilter);
    }

    if (resolvedQuery) {
      params.set("q", resolvedQuery);
    }

    const response = await api.get(`/followups/resolved?${params.toString()}`);
    setResolvedCases(response.data.data || []);
    setResolvedPage(response.data.page || 1);
    setResolvedTotalPages(response.data.totalPages || 1);
  }, [resolvedByFilter, resolvedQuery]);

  useEffect(() => {
    fetchResolved(1);
  }, [fetchResolved]);

  if (!data) return <Layout><p>Carregando...</p></Layout>;

  const chartData = {
    labels: Object.keys(data.steps).map((step) => getStepLabel(step)),
    datasets: [
      {
        label: "Casos por Etapa",
        data: Object.values(data.steps),
        backgroundColor: "rgba(30, 58, 138, 0.7)",
      },
    ],
  };

  const evasionByClassLabels = (data.evasionByClass || []).map(
    (item) => item.className
  );
  const evasionByClassValues = (data.evasionByClass || []).map(
    (item) => Number(item.total)
  );

  const evasionByClassChart = {
    labels: evasionByClassLabels,
    datasets: [
      {
        label: "Evasões por Turma",
        data: evasionByClassValues,
        backgroundColor: "rgba(220, 38, 38, 0.7)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const evasionByClassOptions = {
    ...chartOptions,
    onClick: (_event, elements) => {
      if (!elements?.length) return;
      const index = elements[0].index;
      const classItem = (data.evasionByClass || [])[index];
      if (!classItem?.classId) return;
      navigate(`/analytics/evasions?classId=${classItem.classId}`);
    },
  };

  function formatDelta(current, previous) {
    const diff = current - previous;
    const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
    return sign + Math.abs(diff);
  }

  function formatRateDelta(current, previous) {
    const diff = Number((current - previous).toFixed(2));
    const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
    return sign + Math.abs(diff) + "%";
  }

  return (
    <Layout>
      <div className="container" style={{ maxWidth: "1080px" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">        <div>         <h4 className="mb-1">Dashboard Analítico</h4>          <small className="text-muted">Visão geral dos indicadores</small>       </div>      <button className="btn btn-outline-primary" onClick={() => navigate("/analytics/attendance-report")}>          Relatorio de Frequência         </button>       </div>

        <div className="kpi-grid mb-4">
          {[
            { label: "Em Monitoramento", value: data.kpis.monitoring },
            { label: "Resolvidos no Mês", value: data.kpis.resolvedMonth },
            { label: "Evasões Confirmadas", value: data.kpis.evasion },
            { label: "Acompanhamento Ativo", value: data.kpis.activeFollowUps },
          ].map((item, index) => (
            <div className="kpi-card" key={index}>
              <div className="kpi-title">{item.label}</div>
              <div className="kpi-value kpi-accent">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="card shadow-sm p-3 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Taxa de Risco</h5>
            <small className="text-muted">
              Base: {data.risk?.totalActiveStudents || 0} alunos ativos
            </small>
          </div>

          <div className="row g-3">
            <div className="col-md-4">
              <div className="p-3 border rounded">
                <div className="text-muted">Taxa Atual</div>
                <div className="fs-4 fw-bold">{data.risk?.rate || 0}%</div>
                <div className="text-muted">
                  {data.risk?.atRiskStudents || 0} alunos em risco
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-3 border rounded">
                <div className="text-muted">Semana Atual vs Anterior</div>
                <div className="fs-5 fw-bold">
                  {data.risk?.week?.currentCount || 0} casos
                </div>
                <div className="text-muted">
                  {formatDelta(
                    data.risk?.week?.currentCount || 0,
                    data.risk?.week?.previousCount || 0
                  )} vs semana anterior
                </div>
                <div className="text-muted">
                  {formatRateDelta(
                    data.risk?.week?.currentRate || 0,
                    data.risk?.week?.previousRate || 0
                  )} na taxa
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-3 border rounded">
                <div className="text-muted">Mês Atual vs Anterior</div>
                <div className="fs-5 fw-bold">
                  {data.risk?.month?.currentCount || 0} casos
                </div>
                <div className="text-muted">
                  {formatDelta(
                    data.risk?.month?.currentCount || 0,
                    data.risk?.month?.previousCount || 0
                  )} vs mês anterior
                </div>
                <div className="text-muted">
                  {formatRateDelta(
                    data.risk?.month?.currentRate || 0,
                    data.risk?.month?.previousRate || 0
                  )} na taxa
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm p-3">
          <div style={{ height: "340px" }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>

        <div className="card shadow-sm p-3 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Evasão por Turma</h5>
            <div className="text-muted">
              Semana: {data.evasion?.week?.current || 0} (
              {formatDelta(
                data.evasion?.week?.current || 0,
                data.evasion?.week?.previous || 0
              )}
              )
              {" | "}Mes: {data.evasion?.month?.current || 0} (
              {formatDelta(
                data.evasion?.month?.current || 0,
                data.evasion?.month?.previous || 0
              )}
              )
            </div>
          </div>

          {evasionByClassLabels.length === 0 ? (
            <p className="text-muted mb-0">
              Nenhuma evasão confirmada por turma no momento.
            </p>
          ) : (
            <div style={{ height: "340px" }}>
              <Bar data={evasionByClassChart} options={evasionByClassOptions} />
            </div>
          )}
          <small className="text-muted d-block mt-2">
            Clique em uma barra para ver os alunos evadidos da turma.
          </small>
        </div>
        <div className="card shadow-sm p-3 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Casos Resolvidos</h5>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <input
              className="form-control"
              style={{ maxWidth: "280px" }}
              placeholder="Buscar aluno (nome ou matrí­cula)"
              value={resolvedSearch}
              onChange={(e) => setResolvedSearch(e.target.value)}
            />

            <select
              className="form-select"
              style={{ maxWidth: "220px" }}
              value={resolvedByFilter}
              onChange={(e) => setResolvedByFilter(e.target.value)}
            >
              <option value="">Resolvido por (todos)</option>
              <option value="INSTRUCTOR">Instrutor</option>
              <option value="COORDINATION">Coordenação</option>
            </select>

            <button
              className="btn btn-outline-primary"
              onClick={() => setResolvedQuery(resolvedSearch.trim())}
            >
              Filtrar
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Aluno</th>
                  <th>Turma</th>
                  <th>Resolvido em</th>
                  <th>Resolvido por</th>
                </tr>
              </thead>
              <tbody>
                {resolvedCases.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Nenhum caso resolvido encontrado.
                    </td>
                  </tr>
                ) : (
                  resolvedCases.map((followUp) => (
                    <tr key={followUp.id}>
                      <td>{followUp.student?.name || "N/A"}</td>
                      <td>{followUp.student?.class?.name || "N/A"}</td>
                      <td>
                        {followUp.closedAt
                          ? new Date(followUp.closedAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        {followUp.resolvedBy === "INSTRUCTOR"
                          ? "Instrutor"
                          : followUp.resolvedBy === "COORDINATION"
                          ? "Coordenação"
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-center align-items-center mt-2">
            <button
              className="btn btn-outline-secondary me-3"
              disabled={resolvedPage <= 1}
              onClick={() => fetchResolved(resolvedPage - 1)}
            >
              Anterior
            </button>

            <span>
              PÃ¡gina {resolvedPage} de {resolvedTotalPages}
            </span>

            <button
              className="btn btn-outline-secondary ms-3"
              disabled={resolvedPage >= resolvedTotalPages}
              onClick={() => fetchResolved(resolvedPage + 1)}
            >
              PrÃ³xima
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}







